import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Download, Eye, FileCheck2, FileText, Filter, LockKeyhole, MailCheck, MailOpen, Pencil, RefreshCw, RotateCcw, Upload, UserRound, UsersRound, X } from "lucide-react";

import { API_GET_APPRAISAL_DOCUMENT } from "../../../config/Endpoints.js";
import DocumentViewer from "../../../components/DocumentViewer.jsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import { Button, DateRange, Modal, Select, TextArea } from "../../components/ui/index.ts";
import { ResponsiveDataTable, type ResponsiveColumn } from "../../components/ui/responsive-table/responsive-table.tsx";
import { AppraisalAnnexureReview, type AppraisalAnnexureReviewData } from "../../components/features/EmployeeAppraisal/components/appraisal-annexure-review.tsx";
import { PreviewAppraisalLetter } from "../../components/features/EmployeeAppraisal/components/preview-appraisal-letter.tsx";
import { appraisalLetterPdfFilename, downloadAppraisalLetterPdf } from "../../components/features/EmployeeAppraisal/appraisal-letter-preview.ts";
import { bulkUpdateAppraisalStatus, decideAppraisal, detailPayload, exportAppraisals, getAppraisal, searchAppraisals, updateAppraisal, uploadAppraisalPdf } from "../../components/features/appraisal-workflow/appraisal-api.ts";
import { toAppraisalLetterPreviewData } from "../../components/features/appraisal-workflow/appraisal-annexure-review-data.ts";
import { AppraisalEmployeeAvatar, formatDate, StatusBadge, useAppraisalSearch } from "../../components/features/appraisal-workflow/workflow-shared.tsx";
import type { AppraisalDetail, AppraisalRow } from "../../components/features/appraisal-workflow/types.ts";
import { useEmployeeNameOptions } from "../../hooks/use-employee-name-options.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import { getApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import styles from "../../components/features/appraisal-workflow/appraisal-workflow.module.css";
import type { CompensationComponentKey } from "../../components/features/EmployeeAppraisal/compensation-components.ts";
import { annualAmountToRoundedMonthly, annualBenefitsFromBasic, APPRAISAL_FIXED_AMOUNTS } from "../../components/features/EmployeeAppraisal/appraisal-annexure-utils.ts";
import { useUser } from "../../lib/rbac/useUser.ts";

const LETTER_TYPE_OPTIONS = [
  { value: "APPRAISAL", label: "Appraisal Letter" },
  { value: "PROMOTION", label: "Promotion Letter" },
  { value: "APPRAISAL_WITH_PROMOTION", label: "Appraisal with Promotion Letter" },
];

const APPROVAL_SUMMARY = [
  { status: "DRAFT", label: "Draft", icon: RefreshCw, tone: "blue" },
  { status: "", label: "Pending Approval", icon: UsersRound, tone: "red" },
  { status: "APPROVED", label: "Approved", icon: FileCheck2, tone: "green" },
  { status: "REJECTED", label: "Rejected", icon: RefreshCw, tone: "red" },
  { status: "PDF_GENERATED", label: "Pending Email Scheduled", icon: MailCheck, tone: "orange" },
  { status: "EMAIL_SCHEDULED", label: "Email Queued", icon: MailCheck, tone: "purple" },
  { status: "EMAIL_SENT", label: "Email Delivered", icon: MailOpen, tone: "blue" },
] as const;

const STATUS_FILTER_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "", label: "Pending Approval" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
    { value: "PENDING_EMAIL_SCHEDULED", label: "Pending Email Scheduled" },
  { value: "EMAIL_SCHEDULED", label: "Email Queued" },
  { value: "EMAIL_SENT", label: "Email Delivered" },
];

type ApprovalTableRow = AppraisalRow & Record<string, unknown>;
type DraftFilters = { employeeId: string; letterType: string; status: string; startDate: Date | null; endDate: Date | null };
type DocumentDetail = { url: string; type: string; name: string };
const EMPTY_DOCUMENT: DocumentDetail = { url: "", type: "", name: "" };
const emptyFilters = (status: string): DraftFilters => ({ employeeId: "", letterType: "", status, startDate: null, endDate: null });
const COMPENSATION_EDITABLE_KEYS = new Set<CompensationComponentKey>(["pfEmployeePart", "internetReimbursement", "pfEmployerPart"]);

function localIso(date: Date | null): string {
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function letterTypeLabel(value: string): string {
  return LETTER_TYPE_OPTIONS.find((option) => option.value === value)?.label
    ?? value.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusFilterValue(value: string): string {
  return value === "PENDING_EMAIL_SCHEDULED" ? "PDF_GENERATED" : value;
}

function shouldUseHrStatus(status: string): boolean {
  return !["PENDING", "PENDING_APPROVAL", "APPROVED", "REJECTED", "DRAFT"].includes(status);
}

function hasAppraisalRole(roles: readonly string[] | undefined, role: string): boolean {
  const expected = role.trim().toLowerCase();
  return roles?.some((item) => String(item).trim().toLowerCase() === expected) ?? false;
}

function normalizedPdfBaseName(filename: string): string {
  return filename.replace(/\.pdf$/i, "").replace(/\s*\(\d+\)$/i, "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

function isEmployeeAppraisalPdf(file: File, employeeName: string): boolean {
  const expected = appraisalLetterPdfFilename(employeeName);
  const employeeTokens = employeeName.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  const filenameTokens = file.name.replace(/\.pdf$/i, "").toLowerCase().match(/[a-z0-9]+/g) ?? [];
  return file.name.toLowerCase().endsWith(".pdf") &&
    (!file.type || file.type === "application/pdf") &&
    employeeTokens.length > 0 &&
    employeeTokens.every((token) => filenameTokens.includes(token)) &&
    normalizedPdfBaseName(file.name) === normalizedPdfBaseName(expected);
}

function formatScheduledAt(value?: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function toAnnexureReviewData(detail: AppraisalDetail): AppraisalAnnexureReviewData {
  const basic = Math.ceil(detail.compensation?.basic ?? 0);
  const components = {
    basic,
    pfEmployeePart: Math.ceil(detail.compensation?.pfEmployeePart ?? APPRAISAL_FIXED_AMOUNTS.pfEmployeePart),
    specialAllowance: Math.ceil(detail.allowances?.specialAllowance ?? 0),
    houseRentAllowance: Math.ceil(detail.allowances?.houseRentAllowance ?? 0),
    internetReimbursement: Math.ceil(detail.allowances?.internetReimbursement ?? APPRAISAL_FIXED_AMOUNTS.internetReimbursement),
    gratuity: Math.ceil((basic / 2) / 12),
    pfEmployerPart: Math.ceil(detail.statutory?.pfEmployerPart ?? APPRAISAL_FIXED_AMOUNTS.pfEmployerPart),
    annualFixedComponent: detail.annualComponent?.enabled ? Math.ceil(detail.annualComponent.amount ?? 0) : 0,
  };
  const monthlyGross = detail.baseValue ?? components.basic + components.pfEmployeePart + components.specialAllowance + components.houseRentAllowance + components.internetReimbursement;
  const monthlyBenefits = components.gratuity + components.pfEmployerPart;
  return {
    components,
    monthlyGross,
    annualGross: monthlyGross * 12,
    monthlyBenefits,
    annualBenefits: annualBenefitsFromBasic(basic, components.pfEmployerPart),
    monthlyCtc: Math.ceil(monthlyGross + monthlyBenefits + annualAmountToRoundedMonthly(components.annualFixedComponent)),
    annualCtc: monthlyGross * 12 + annualBenefitsFromBasic(basic, components.pfEmployerPart) + components.annualFixedComponent,
    annualComponentEnabled: detail.annualComponent?.enabled,
    remarks: detail.annualComponent?.remarks,
  };
}

function recalculateAnnexure(
  detail: AppraisalDetail,
  monthlyGross: number,
  overrides: Partial<Record<CompensationComponentKey, number>> = {},
): AppraisalDetail {
  const current = toAnnexureReviewData(detail).components;
  const pfEmployeePart = overrides.pfEmployeePart ?? current.pfEmployeePart;
  const internetReimbursement = overrides.internetReimbursement ?? current.internetReimbursement;
  const pfEmployerPart = overrides.pfEmployerPart ?? current.pfEmployerPart;
  const basic = Math.ceil(Math.max(0, (monthlyGross - internetReimbursement) * 0.5));
  const houseRentAllowance = Math.ceil(basic / 2);
  const specialAllowance = Math.ceil(Math.max(
    0,
    monthlyGross - basic - pfEmployeePart - houseRentAllowance - internetReimbursement,
  ));

  return {
    ...detail,
    baseValue: monthlyGross,
    compensation: { ...(detail.compensation ?? {}), basic, pfEmployeePart },
    allowances: {
      ...(detail.allowances ?? {}),
      specialAllowance,
      houseRentAllowance,
      internetReimbursement,
    },
    statutory: {
      ...(detail.statutory ?? {}),
      gratuity: Math.ceil((basic / 2) / 12),
      pfEmployerPart,
    },
  };
}

export default function AppraisalApprovalPage() {
  const navigate = useNavigate();
  const user = useUser();
  const isAppraisalApprover = hasAppraisalRole(user?.roles, "Appraisal Approver");
  const isAppraisalInitiator =
    !isAppraisalApprover && hasAppraisalRole(user?.roles, "Appraisal Initiator");
  const defaultStatus = isAppraisalInitiator ? "APPROVED" : "PENDING_APPROVAL";
  const search = useAppraisalSearch(defaultStatus, isAppraisalInitiator);
  const employees = useEmployeeNameOptions();
  const [draftFilters, setDraftFilters] = useState<DraftFilters>(() =>
    emptyFilters(isAppraisalInitiator ? "APPROVED" : ""),
  );
  const [detail, setDetail] = useState<AppraisalDetail | null>(null);
  const [annexureBeforeEdit, setAnnexureBeforeEdit] = useState<AppraisalDetail | null>(null);
  const [detailViewMode, setDetailViewMode] = useState<"review" | "preview">("review");
  const [reviewLoadingId, setReviewLoadingId] = useState<number | null>(null);
  const [editingAnnexure, setEditingAnnexure] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string | number>>(new Set());
  const [decision, setDecision] = useState<"APPROVE" | "REJECT" | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [draftSubmitConfirmOpen, setDraftSubmitConfirmOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [downloadedAppraisalIds, setDownloadedAppraisalIds] = useState<Set<number>>(new Set());
  const [viewLoadingId, setViewLoadingId] = useState<number | null>(null);
  const [documentDetail, setDocumentDetail] = useState<DocumentDetail>(EMPTY_DOCUMENT);

  const usesHrStatusFor = (status: string) =>
    isAppraisalInitiator || shouldUseHrStatus(status);

  const loadCounts = async () => {
    const statuses = Array.from(new Set(APPROVAL_SUMMARY.map(({ status }) => status)));
    const entries = await Promise.all(statuses.map(async (status) => [
      status || "ALL",
      (await searchAppraisals({ employeeId: "", status: status || "PENDING_APPROVAL", useHrStatus: usesHrStatusFor(status || "PENDING_APPROVAL"), letterType: "", fromDate: "", toDate: "", page: 0, size: 1 })).totalElements,
    ] as const));
    setCounts(Object.fromEntries(entries));
  };

  useEffect(() => { if (search.error) showError(search.error); }, [search.error]);
  useEffect(() => { void loadCounts().catch(() => undefined); }, []);

  const review = async (row: AppraisalRow) => {
    setReviewLoadingId(row.id);
    try {
      const loaded = await getAppraisal(row.id);
      const merged = { ...row, ...loaded, id: loaded.id ?? row.id, employeeCode: row.employeeCode, designation: row.designation };
      setDetailViewMode("review");
      setAnnexureBeforeEdit(null);
      setDetail(merged);
      setSelectedPdf(null);
      setPdfUploaded(Boolean(merged.generatedPdf) || ["PDF_GENERATED", "EMAIL_SCHEDULED", "EMAIL_SENT"].includes(merged.status));
      setEditingAnnexure(false);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to load appraisal.");
    } finally {
      setReviewLoadingId(null);
    }
  };

  const previewAnnexure = async (row: AppraisalRow) => {
    setReviewLoadingId(row.id);
    try {
      const loaded = await getAppraisal(row.id);
      setDetailViewMode("preview");
      setAnnexureBeforeEdit(null);
      setDetail({
        ...row,
        ...loaded,
        id: loaded.id ?? row.id,
        employeeCode: row.employeeCode,
        designation: row.designation,
      });
      setEditingAnnexure(false);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to load appraisal annexure.");
    } finally {
      setReviewLoadingId(null);
    }
  };

  const continueDraft = async (row: AppraisalRow) => {
    if (!isAppraisalInitiator) return;
    setReviewLoadingId(row.id);
    try {
      const loaded = await getAppraisal(row.id);
      navigate("/dashboard/appraisal", {
        state: {
          draftAppraisal: {
            ...row,
            ...loaded,
            id: loaded.id ?? row.id,
          },
        },
      });
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to load the saved draft.");
    } finally {
      setReviewLoadingId(null);
    }
  };

  const clearDocumentDetail = () => {
    setDocumentDetail((current) => {
      if (current.url) URL.revokeObjectURL(current.url);
      return EMPTY_DOCUMENT;
    });
  };

  const viewSignedPdf = async (row: ApprovalTableRow) => {
    if (!row.signedPdfPath) {
      showError("Signed appraisal PDF is not available.");
      return;
    }
    setViewLoadingId(row.id);
    try {
      const url = `${API_GET_APPRAISAL_DOCUMENT}?filepath=${encodeURIComponent(row.signedPdfPath)}`;
      const { data } = await getApi<Blob>(url, accessToken(), "blob");
      const contentType = data.type || "application/pdf";
      const fileUrl = URL.createObjectURL(data);
      const fileName = row.signedPdfPath.split("/").filter(Boolean).pop()?.replace(/\.pdf$/i, "") || "Signed Appraisal Letter";
      setDocumentDetail((current) => {
        if (current.url) URL.revokeObjectURL(current.url);
        return { url: fileUrl, type: contentType, name: fileName };
      });
      window.setTimeout(() => window.document.getElementById("open-appraisal-approval-document")?.click(), 0);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to open the signed appraisal PDF.");
    } finally {
      setViewLoadingId(null);
    }
  };

  const columns: ResponsiveColumn<ApprovalTableRow>[] = [
    {
      id: "employeeName", header: "Employee Name", accessor: "employeeName", sortable: true,
      render: (row) => <div className={styles.employeeCell}><AppraisalEmployeeAvatar employeeName={row.employeeName} photoPath={row.photoPath} /><div className={styles.employeeIdentity}><strong>{row.employeeName}</strong><small>{row.designation || "-"}</small></div></div>,
      getTitleText: (row) => row.employeeName,
    },
    { id: "letterType", header: "Letter Type", accessor: "letterType", sortable: true, render: (row) => <span className={styles.letterBadge}>{letterTypeLabel(row.letterType)}</span>, nowrap: true },
    { id: "submittedOn", header: "Submitted On", sortable: true, getSortValue: (row) => row.updatedAt ?? row.createdAt ?? "", render: (row) => formatDate(row.updatedAt ?? row.createdAt), nowrap: true },
    { id: "status", header: "Status", accessor: isAppraisalInitiator ? "hrStatus" : "cpoStatus", sortable: true, render: (row) => <StatusBadge status={isAppraisalInitiator ? row.hrStatus : row.cpoStatus} />, nowrap: true },
    ...(["EMAIL_SCHEDULED", "EMAIL_SENT"].includes(search.filters.status) ? [{ id: "scheduledAt", header: "Scheduled For", accessor: "scheduledAt", sortable: true, render: (row: ApprovalTableRow) => formatScheduledAt(row.scheduledAt), nowrap: true } satisfies ResponsiveColumn<ApprovalTableRow>] : []),
    ...(isAppraisalApprover && search.filters.status === "PENDING_APPROVAL" ? [
      { id: "action", header: "Letter", render: (row: ApprovalTableRow) => <Button size="sm" variant="muted-outline" disabled={reviewLoadingId != null} onClick={() => void previewAnnexure(row)}>View Letter</Button>, nowrap: true } satisfies ResponsiveColumn<ApprovalTableRow>,
      { id: "letterToReview", header: "Action", render: (row: ApprovalTableRow) => <Button size="sm" variant="accent" disabled={reviewLoadingId != null} onClick={() => void review(row)}>{reviewLoadingId === row.id ? "Loading..." : "Annexure"}</Button>, nowrap: true } satisfies ResponsiveColumn<ApprovalTableRow>,
    ] : [
      { id: "action", header: "Action", render: (row: ApprovalTableRow) => {
        if (search.filters.status === "PDF_GENERATED") {
          return <Button size="sm" variant="muted-outline" startIcon={<Eye size={15} />} disabled={!row.signedPdfPath || viewLoadingId != null} onClick={() => void viewSignedPdf(row)}>{viewLoadingId === row.id ? "Opening..." : "View"}</Button>;
        }
        if (isAppraisalInitiator && search.filters.status === "DRAFT") {
          return <Button size="sm" variant="muted-outline" disabled={reviewLoadingId != null} onClick={() => void continueDraft(row)}>{reviewLoadingId === row.id ? "Loading..." : "View"}</Button>;
        }
        const pending = row.cpoStatus === "PENDING_APPROVAL";
        const label = isAppraisalInitiator
          ? pending ? "View Annexure" : row.cpoStatus === "APPROVED" ? "View" : "View Annexure"
          : pending ? "Review" : "View Annexure";
        return <Button size="sm" variant="muted-outline" disabled={reviewLoadingId != null} onClick={() => void review(row)}>{reviewLoadingId === row.id ? "Loading..." : label}</Button>;
      }, nowrap: true } satisfies ResponsiveColumn<ApprovalTableRow>,
    ]),
  ];

  const applyFilters = () => { const status = statusFilterValue(draftFilters.status) || "PENDING_APPROVAL"; setSelectedRowIds(new Set()); search.update({ employeeId: draftFilters.employeeId, letterType: draftFilters.letterType, status, useHrStatus: usesHrStatusFor(status), fromDate: localIso(draftFilters.startDate), toDate: localIso(draftFilters.endDate), page: 0 }); };
  const clearFilters = () => { setDraftFilters(emptyFilters(isAppraisalInitiator ? "APPROVED" : "")); setSelectedRowIds(new Set()); search.update({ employeeId: "", letterType: "", status: defaultStatus, useHrStatus: isAppraisalInitiator, fromDate: "", toDate: "", page: 0 }); };

  const exportRows = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await exportAppraisals({
        employeeId: search.filters.employeeId,
        status: search.filters.status || "PENDING_APPROVAL",
        letterType: search.filters.letterType,
        fromDate: search.filters.fromDate,
        toDate: search.filters.toDate,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSuccess("Appraisal export downloaded successfully.");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to export appraisals.");
    } finally {
      setExporting(false);
    }
  };
  const closeReview = () => { setDetail(null); setAnnexureBeforeEdit(null); setDetailViewMode("review"); setDecision(null); setComment(""); setEditingAnnexure(false); setSelectedPdf(null); setPdfUploaded(false); };
  const refresh = async () => { await Promise.all([search.load(), loadCounts()]); };
  const downloadPdf = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await downloadAppraisalLetterPdf(toAppraisalLetterPreviewData(detail));
      setDownloadedAppraisalIds((current) => new Set(current).add(detail.id));
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to prepare the appraisal letter.");
    } finally {
      setBusy(false);
    }
  };
  const selectFinalizedPdf = (file: File | null, input: HTMLInputElement) => {
    if (!file) { setSelectedPdf(null); return; }
    if (!detail || !downloadedAppraisalIds.has(detail.id)) {
      setSelectedPdf(null);
      input.value = "";
      showError(`Download the appraisal letter for ${detail?.employeeName ?? "this employee"} before selecting a file.`);
      return;
    }
    if (!isEmployeeAppraisalPdf(file, detail.employeeName)) {
      setSelectedPdf(null);
      input.value = "";
      showError(`Upload the appraisal letter downloaded for ${detail.employeeName}. Expected file: ${appraisalLetterPdfFilename(detail.employeeName)}`);
      return;
    }
    setSelectedPdf(file);
  };
  const markAsReady = async () => {
    if (!detail || !selectedPdf) return;
    setBusy(true);
    try {
      const response = await uploadAppraisalPdf(detail.id, selectedPdf);
      showSuccess(response.message ?? "Appraisal marked as ready.");
      closeReview();
      await refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to mark the appraisal as ready.");
    } finally {
      setBusy(false);
    }
  };
  const openDecision = (nextDecision: "APPROVE" | "REJECT") => {
    setComment("");
    setDecision(nextDecision);
  };
  const closeDecision = () => {
    if (busy) return;
    setDecision(null);
    setComment("");
  };
  const updateAnnexureComponent = (key: CompensationComponentKey, value: number) => {
    if (!COMPENSATION_EDITABLE_KEYS.has(key)) return;
    setDetail((current) => current ? recalculateAnnexure(current, toAnnexureReviewData(current).monthlyGross, { [key]: value }) : current);
  };

  const updateMonthlyGross = (value: number) => {
    setDetail((current) => current ? recalculateAnnexure(current, value) : current);
  };

  const updateAnnualFixedComponent = (value: number) => {
    setDetail((current) => current ? { ...current, annualComponent: { ...(current.annualComponent ?? {}), amount: value } } : current);
  };

  const saveEditedAnnexure = async () => {
    if (!isAppraisalApprover || !detail) return;
    setBusy(true);
    try {
      const reviewData = toAnnexureReviewData(detail);
      const normalizedDetail = {
        ...detail,
        compensation: {
          ...(detail.compensation ?? {}),
          basic: reviewData.components.basic,
          pfEmployeePart: reviewData.components.pfEmployeePart,
        },
        allowances: {
          ...(detail.allowances ?? {}),
          specialAllowance: reviewData.components.specialAllowance,
          houseRentAllowance: reviewData.components.houseRentAllowance,
          internetReimbursement: reviewData.components.internetReimbursement,
        },
        statutory: {
          gratuity: reviewData.components.gratuity,
          pfEmployerPart: reviewData.components.pfEmployerPart,
        },
      };
      const response = await updateAppraisal(detail.id, {
        ...detailPayload(normalizedDetail),
        designationId: normalizedDetail.designationId ?? null,
        status: detail.status,
        hrStatus: "PENDING_APPROVAL",
        cpoStatus: "PENDING_APPROVAL",
      });
      showSuccess(response.message ?? "Annexure updated successfully.");
      setDetail(normalizedDetail);
      setAnnexureBeforeEdit(null);
      setEditingAnnexure(false);
      await refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to update annexure.");
    } finally { setBusy(false); }
  };

  const startEditingAnnexure = () => {
    if (!detail) return;
    setAnnexureBeforeEdit(detail);
    setEditingAnnexure(true);
  };

  const cancelEditingAnnexure = () => {
    if (annexureBeforeEdit) setDetail(annexureBeforeEdit);
    setAnnexureBeforeEdit(null);
    setEditingAnnexure(false);
  };

  const submitDecision = async () => {
    if (!isAppraisalApprover || !detail || !decision) return;
    if (decision === "REJECT" && !comment.trim()) {
      showError("A rejection reason is required.");
      return;
    }
    setBusy(true);
    try {
      const reviewData = toAnnexureReviewData(detail);
      const normalizedDetail = {
        ...detail,
        compensation: {
          ...(detail.compensation ?? {}),
          basic: reviewData.components.basic,
          pfEmployeePart: reviewData.components.pfEmployeePart,
        },
        allowances: {
          ...(detail.allowances ?? {}),
          specialAllowance: reviewData.components.specialAllowance,
          houseRentAllowance: reviewData.components.houseRentAllowance,
          internetReimbursement: reviewData.components.internetReimbursement,
        },
        statutory: {
          gratuity: reviewData.components.gratuity,
          pfEmployerPart: reviewData.components.pfEmployerPart,
        },
      };
      const response = await decideAppraisal(
        detail.id,
        decision,
        comment.trim() || "Approved",
        detailPayload(normalizedDetail),
      );
      showSuccess(response.message ?? `Appraisal ${decision === "APPROVE" ? "approved" : "rejected"}.`);
      setSelectedRowIds((current) => {
        const next = new Set(current);
        next.delete(detail.id);
        return next;
      });
      closeReview();
      await refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Decision failed.");
    } finally {
      setBusy(false);
    }
  };

  const bulkApprove = async () => {
    if (!isAppraisalApprover) return;
    const ids = Array.from(selectedRowIds).map(Number).filter(Number.isFinite);
    if (!ids.length) return;
    setBusy(true);
    const results = await Promise.allSettled(ids.map((id) => decideAppraisal(id, "APPROVE", "Approved", null)));
    const approvedIds = ids.filter((_, index) => results[index]?.status === "fulfilled");
    const failedCount = ids.length - approvedIds.length;
    setSelectedRowIds((current) => { const next = new Set(current); approvedIds.forEach((id) => next.delete(id)); return next; });
    setBulkConfirmOpen(false);
    setBusy(false);
    if (approvedIds.length) showSuccess(`${approvedIds.length} appraisal${approvedIds.length === 1 ? "" : "s"} approved successfully.`);
    if (failedCount) showError(`${failedCount} appraisal${failedCount === 1 ? "" : "s"} could not be approved.`);
    await Promise.all([search.load(), loadCounts()]);
  };

  const submitSelectedDrafts = async () => {
    if (!isAppraisalInitiator) return;
    const appraisalIds = Array.from(selectedRowIds).map(Number).filter(Number.isFinite);
    if (!appraisalIds.length) return;
    setBulkSubmitting(true);
    try {
      const response = await bulkUpdateAppraisalStatus(appraisalIds, "PENDING_APPROVAL");
      showSuccess(response.message ?? `${appraisalIds.length} appraisal${appraisalIds.length === 1 ? "" : "s"} submitted for approval.`);
      setSelectedRowIds(new Set());
      setDraftSubmitConfirmOpen(false);
      await refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to submit the selected appraisals for approval.");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const showsLetterPreview =
    isAppraisalInitiator ||
    (isAppraisalApprover && detail?.cpoStatus === "APPROVED") ||
    detailViewMode === "preview";

  return <PageShell><div className={`${styles.approvalPage} ${styles.approvalPageClosed}`}>
    <div className={`${styles.approvalSummaryGrid} ${styles.approvalSummaryContentWidth}`}>{APPROVAL_SUMMARY.map(({ status, label, icon: Icon, tone }) => {
      const cardStatus = status || "PENDING_APPROVAL";
      const active = isAppraisalInitiator
        ? cardStatus === "APPROVED"
        : isAppraisalApprover && cardStatus === "PENDING_APPROVAL";
      const activeTone = active
        ? cardStatus === "APPROVED" ? styles.summaryCardActiveGreen : cardStatus === "PENDING_APPROVAL" ? styles.summaryCardActiveRed : styles.summaryCardActive
        : "";
      return <div key={`${status || "all"}-${label}`} className={`${styles.summaryCard} ${styles.summaryCardStatic} ${activeTone}`}><span className={`${styles.summaryIcon} ${styles[`tone${tone}`]}`}><Icon size={20} /></span><span><strong>{label}</strong><b>{counts[status || "ALL"] ?? "--"}</b></span></div>;
    })}</div>
    <section className={styles.listPanel}>
      <Card className={styles.approvalFiltersCard}><div className={`${styles.approvalFiltersGrid} ${styles.managementFiltersGrid}`}>
        <Select label="Employee Name" placeholder={employees.loading ? "Loading Employees..." : "Select Employee Name"} searchable isClearable options={employees.options} value={draftFilters.employeeId} disabled={employees.loading || search.loading} onChange={(employeeId) => setDraftFilters((current) => ({ ...current, employeeId }))} />
        <DateRange label="Effective Date" placeholderText="Select Effective Date" startDate={draftFilters.startDate} endDate={draftFilters.endDate} disabled={search.loading} onChange={([startDate, endDate]) => setDraftFilters((current) => ({ ...current, startDate, endDate }))} />
        <Select label="Letter Type" placeholder="Select Letter Type" isClearable options={LETTER_TYPE_OPTIONS} value={draftFilters.letterType} disabled={search.loading} onChange={(letterType) => setDraftFilters((current) => ({ ...current, letterType }))} />
        <Select label="Status" placeholder="Select Status" options={STATUS_FILTER_OPTIONS} value={draftFilters.status} disabled={search.loading} onChange={(status) => setDraftFilters((current) => ({ ...current, status }))} />
        <div className={styles.approvalFilterActions}><Button variant="accent" startIcon={<Filter size={16} />} disabled={search.loading} onClick={applyFilters}>Apply Filter</Button><Button variant="light-grey" startIcon={<RotateCcw size={16} />} disabled={search.loading} onClick={clearFilters}>Clear Filter</Button>
        <Button variant="muted-outline" startIcon={<Download size={16} />} disabled={search.loading || exporting} onClick={() => void exportRows()}>{exporting ? "Exporting..." : "Export"}</Button>
        </div>
      </div></Card>
      <ResponsiveDataTable<ApprovalTableRow>
        aria-label="Pending appraisal approvals" columns={columns} data={search.rows as ApprovalTableRow[]}
        selection={isAppraisalInitiator && search.filters.status === "DRAFT"
          ? { getRowId: (row) => row.id, isRowDisabled: (row) => row.hrStatus !== "DRAFT", selectedRowIds, onSelectionChange: setSelectedRowIds, ariaLabel: "Select all draft appraisals on this page" }
          : isAppraisalApprover && search.filters.status === "PENDING_APPROVAL"
            ? { getRowId: (row) => row.id, isRowDisabled: (row) => row.cpoStatus !== "PENDING_APPROVAL", selectedRowIds, onSelectionChange: setSelectedRowIds, ariaLabel: "Select all pending appraisals on this page" }
            : undefined}
        page={search.filters.page + 1} pageSize={search.filters.size} totalItems={search.totalElements}
        onPageChange={(page) => search.update({ page: page - 1 })} onPageSizeChange={(size) => search.update({ size, page: 0 })}
        emptyState={<div className={styles.approvalEmptyState}>{search.loading ? "Loading Records..." : "No records available"}</div>}
        totalLabel={`Total Records: ${search.totalElements}`}
      />
      {isAppraisalApprover && selectedRowIds.size ? <div className={styles.bulkActionBar}><span>{selectedRowIds.size} selected</span><Button variant="success" startIcon={<LockKeyhole size={16} />} onClick={() => setBulkConfirmOpen(true)}>Lock and Approve</Button></div> : null}
      {isAppraisalInitiator && search.filters.status === "DRAFT" && selectedRowIds.size ? <div className={styles.bulkActionBar}><span>{selectedRowIds.size} selected</span><Button variant="accent" startIcon={<CheckCircle2 size={16} />} disabled={bulkSubmitting} onClick={() => setDraftSubmitConfirmOpen(true)}>Submit for Approval</Button></div> : null}
    </section>

    <Modal open={detail != null} onClose={closeReview} title={detailViewMode === "preview" ? "Appraisal Annexure" : isAppraisalInitiator || (detail?.cpoStatus === "PENDING_APPROVAL" && isAppraisalApprover) ? "Appraisal Review" : "Appraisal Annexure"} size="xl" className={showsLetterPreview ? styles.managementReviewModal : undefined} backdropClassName={showsLetterPreview ? styles.managementReviewBackdrop : undefined} bodyClassName={showsLetterPreview ? styles.managementReviewModalBody : styles.approvalReviewModalBody} footer={detailViewMode === "review" && isAppraisalApprover && detail?.cpoStatus === "PENDING_APPROVAL" ? <div className={styles.approvalReviewActions}><Button variant="danger" startIcon={<X size={16} />} disabled={busy} onClick={() => openDecision("REJECT")}>Reject</Button><Button variant="success" startIcon={<LockKeyhole size={16} />} disabled={busy} onClick={() => openDecision("APPROVE")}>Lock and Approve</Button></div> : null}>
      {detail && isAppraisalInitiator && detail.cpoStatus === "APPROVED" ? <div className={styles.workflowModalContent}>
        <div className={styles.managementReviewLayout}>
          <aside className={styles.managementControlPanel}>
            <section className={styles.workflowSection}>
              <div className={styles.workflowSectionHeader}><div><strong>Appraisal Details</strong><p>Employee and letter summary.</p></div><StatusBadge status={detail.cpoStatus} /></div>
              <div className={styles.detailGrid}><div><span>Employee</span><strong>{detail.employeeName}</strong></div><div><span>Letter Type</span><strong>{letterTypeLabel(detail.letterType)}</strong></div><div><span>Effective Date</span><strong>{formatDate(detail.effectiveDate)}</strong></div><div><span>Designation</span><strong>{detail.promotedDesignation || detail.designationChangeText || detail.designation || detail.currentDesignation || "-"}</strong></div><div><span>Updated On</span><strong>{formatDate(detail.updatedAt)}</strong></div></div>
              {detail.cpoComments || detail.remarks ? <div className={styles.remarkBox}><strong>Remarks</strong><p>{detail.cpoComments || detail.remarks}</p></div> : null}
            </section>
            <section className={styles.workflowSection}>
              <div className={styles.workflowSectionHeader}><div><strong>Final Appraisal Letter</strong><p>Download the generated letter, then upload the finalized PDF.</p></div>{pdfUploaded ? <span className={styles.uploadComplete}><CheckCircle2 size={16} /> Ready</span> : null}</div>
              <div className={styles.managementFileActions}>
                <Button variant="muted-outline" startIcon={<Download size={16} />} disabled={busy} onClick={() => void downloadPdf()}>{busy ? "Preparing..." : "Download File"}</Button>
                <div className={styles.uploadPanel}><label htmlFor="approval-appraisal-pdf">Upload finalized PDF</label><input id="approval-appraisal-pdf" type="file" accept="application/pdf" disabled={busy} onChange={(event) => selectFinalizedPdf(event.target.files?.[0] ?? null, event.currentTarget)} />{selectedPdf ? <small>{selectedPdf.name}</small> : <small>Upload {appraisalLetterPdfFilename(detail.employeeName)}</small>}{selectedPdf ? <Button startIcon={<Upload size={16} />} disabled={busy} onClick={() => void markAsReady()}>{busy ? "Marking as Ready..." : "Mark as Ready"}</Button> : null}</div>
              </div>
            </section>
          </aside>
          <section className={`${styles.workflowSection} ${styles.managementPreviewPanel}`}><PreviewAppraisalLetter data={toAppraisalLetterPreviewData(detail)} className={styles.workflowLetterPreview} fillHeight /></section>
        </div>
      </div> : detail && showsLetterPreview ? <div className={styles.workflowModalContent}>
        <div className={styles.pendingPreviewLayout}>
          <section className={`${styles.workflowSection} ${styles.managementPreviewPanel}`}>
            <PreviewAppraisalLetter data={toAppraisalLetterPreviewData(detail)} className={styles.workflowLetterPreview} fillHeight />
          </section>
        </div>
      </div> : detail ? <section className={styles.approvalReviewModal}>
        <div className={styles.reviewHeader}><div className={styles.profileSummary}><span className={styles.profileAvatar}><UserRound size={24} /></span><div><h1 className="heading-sm">{detail.employeeName}</h1>{detail.employeeCode ? <p>{detail.employeeCode}</p> : null}<small>{detail.designation || "-"}</small></div></div><div className={styles.reviewStatus}><StatusBadge status={detail.cpoStatus} /></div></div>
        <div className={styles.detailGrid}><div><span className="sub-heading-sm text-accent">Letter Type</span><strong>{letterTypeLabel(detail.letterType)}</strong></div><div><span className="sub-heading-sm text-accent">Letter Issue Date</span><strong>{formatDate(detail.letterIssueDate)}</strong></div><div><span className="sub-heading-sm text-accent">Effective Date</span><strong>{formatDate(detail.effectiveDate)}</strong></div><div><span className="sub-heading-sm text-accent">Notice Period</span><strong>{detail.noticePeriod ?? "-"}</strong></div><div><span className="sub-heading-sm text-accent">Location</span><strong>{detail.workLocation || detail.currentLocation || detail.location || "-"}</strong></div>{detail.promotedDesignation || detail.designationChangeText ? <div><span className="sub-heading-sm text-accent">Promoted To</span><strong>{detail.promotedDesignation || detail.designationChangeText}</strong></div> : null}</div>
        {/* <div className={styles.annualComponentSummary}>
          <div><span>Annual Component</span><strong>{detail.annualComponent?.enabled ? "Yes" : "No"}</strong></div>
        </div> */}
        <div className={styles.reviewToolbar}><h2 className="heading-sm"><FileText size={16} /> Annexure Details</h2>{detailViewMode === "review" && isAppraisalApprover && detail.cpoStatus === "PENDING_APPROVAL" ? editingAnnexure ? <div className="d-flex gap-2"><Button size="sm" variant="muted-outline" disabled={busy} onClick={() => void saveEditedAnnexure()}>{busy ? "Saving..." : "Done"}</Button><Button size="sm" variant="light-grey" disabled={busy} onClick={cancelEditingAnnexure}>Cancel</Button></div> : <Button size="sm" variant="muted-outline" startIcon={<Pencil size={14} />} disabled={busy} onClick={startEditingAnnexure}>Edit Annexure</Button> : null}</div>
        <div className={styles.annexureScroll}><AppraisalAnnexureReview data={toAnnexureReviewData(detail)} editable={editingAnnexure} onComponentChange={updateAnnexureComponent} onMonthlyGrossChange={updateMonthlyGross} onAnnualFixedComponentChange={updateAnnualFixedComponent} /></div>
      </section> : null}
    </Modal>

    <Modal open={decision != null} onClose={closeDecision} title={decision === "REJECT" ? "Reject" : "Approve"} size="sm" footer={<div className={styles.approvalReviewActions}><Button variant="muted-outline" disabled={busy} onClick={closeDecision}>Cancel</Button><Button variant={decision === "REJECT" ? "danger" : "success"} disabled={busy} onClick={() => void submitDecision()}>{busy ? "Submitting..." : "Confirm"}</Button></div>}><p>{decision === "REJECT" ? "Enter the correction required from HR." : "Are you sure you want to lock and approve this appraisal?"}</p><TextArea key={decision ?? "closed"} id="decision-comment" label={decision === "REJECT" ? "Reason / Comment" : "Comment"} required={decision === "REJECT"} rows={4} value={comment} onChange={(event) => setComment(event.target.value)} /></Modal>
    <Modal open={bulkConfirmOpen} onClose={() => { if (!busy) setBulkConfirmOpen(false); }} title="Approve selected appraisals" size="sm" footer={<div className={styles.approvalReviewActions}><Button variant="muted-outline" disabled={busy} onClick={() => setBulkConfirmOpen(false)}>Cancel</Button><Button variant="success" disabled={busy} onClick={() => void bulkApprove()}>{busy ? "Approving..." : "Confirm Approval"}</Button></div>}><p>Are you sure you want to lock and approve {selectedRowIds.size} selected appraisal{selectedRowIds.size === 1 ? "" : "s"}?</p></Modal>
    <Modal open={draftSubmitConfirmOpen} onClose={() => { if (!bulkSubmitting) setDraftSubmitConfirmOpen(false); }} title="Submit appraisals for approval" size="sm" closeOnEscape footer={<div className={styles.approvalReviewActions}><Button variant="light-grey" disabled={bulkSubmitting} onClick={() => setDraftSubmitConfirmOpen(false)}>Cancel</Button><Button disabled={bulkSubmitting} onClick={() => void submitSelectedDrafts()}>{bulkSubmitting ? "Submitting..." : "Confirm Submission"}</Button></div>}><p>Are you sure you want to submit {selectedRowIds.size} selected appraisal{selectedRowIds.size === 1 ? "" : "s"} for approval?</p></Modal>
    <button id="open-appraisal-approval-document" type="button" data-bs-toggle="modal" data-bs-target="#staticBackdropDocumentViewer" style={{ display: "none" }} tabIndex={-1} aria-hidden />
    {documentDetail.url ? <DocumentViewer url={documentDetail.url} contentType={documentDetail.type} fileName={documentDetail.name} onClose={clearDocumentDetail} /> : null}
  </div></PageShell>;
}
